import React, { useMemo } from 'react';
import { ReviewQuestionWrapper } from '../shared/ReviewQuestionWrapper';
import { SequencingCorrectAnswer, SequencingUserAnswer } from '../shared/SequencingSections';
import {
  useSequencingData,
  getChoiceTextByIdx,
  normalizeUserAnswerIndices,
  normalizeCorrectAnswerIndices,
  convertIndicesToText
} from '../../../../../hooks/useSequencingData';

const SequencingReview = ({ question, userAnswer = [], correctAnswer = [], isCorrect, accuracy, explanation = '', recap = '', annotations = [], reportProps = null }) => {
  // Transform backend data using custom hook
  const transformedQuestion = useSequencingData(question);

  // Helper function to get choice text by idx
  const getChoiceTextByIdxFn = useMemo(() => {
    return (idx) => getChoiceTextByIdx(transformedQuestion.choices, idx);
  }, [transformedQuestion.choices]);

  // Normalize user answer indices
  const userAnswerIndices = useMemo(() => {
    return normalizeUserAnswerIndices(userAnswer);
  }, [userAnswer]);
  
  // Convert user answer indices to text
  const userAnswerTexts = useMemo(() => {
    if (userAnswerIndices.length > 0) {
      return convertIndicesToText(userAnswerIndices, getChoiceTextByIdxFn);
    } else if (Array.isArray(userAnswer) && userAnswer.length > 0) {
      return userAnswer.map(item => typeof item === 'string' ? item : (item?.text || String(item)));
    }
    return [];
  }, [userAnswer, userAnswerIndices, getChoiceTextByIdxFn]);

  // Normalize correct answer indices (supports multiple sequences)
  const correctAnswerIndices = useMemo(() => {
    return normalizeCorrectAnswerIndices(correctAnswer);
  }, [correctAnswer]);
  
  // Convert all correct answer sequences to text
  const allCorrectAnswerTexts = useMemo(() => {
    if (correctAnswerIndices.length > 0) {
      return correctAnswerIndices.map(sequence => {
        return convertIndicesToText(sequence, getChoiceTextByIdxFn);
      });
    } else if (Array.isArray(correctAnswer) && correctAnswer.length > 0) {
      if (typeof correctAnswer[0] === 'string') {
        return [correctAnswer];
      } else if (Array.isArray(correctAnswer[0])) {
        return correctAnswer;
      }
    }
    return [];
  }, [correctAnswer, correctAnswerIndices, getChoiceTextByIdxFn]);

  // Check if user answer matches ANY of the correct sequences
  const isAnswerCorrect = useMemo(() => {
    if (isCorrect !== undefined) {
      return Boolean(isCorrect);
    }
    
    if (userAnswerIndices.length === 0 || correctAnswerIndices.length === 0) {
      return false;
    }
    
    return correctAnswerIndices.some(correctSeq => {
      if (userAnswerIndices.length !== correctSeq.length) {
        return false;
      }
      return userAnswerIndices.every((userIdx, i) => {
        const correctIdx = correctSeq[i];
        return Number(userIdx) === Number(correctIdx);
      });
    });
  }, [isCorrect, userAnswerIndices, correctAnswerIndices]);

  return (
    <ReviewQuestionWrapper
      questionType="Sequencing"
      question={question}
      explanation={explanation}
      recap={recap}
      annotations={annotations}
      reportProps={reportProps}
    >
      <div className="text-base text-[var(--text-primary)] leading-relaxed">
        <p>{transformedQuestion.text}</p>
      </div>

      <SequencingCorrectAnswer allCorrectAnswerTexts={allCorrectAnswerTexts} />

      <SequencingUserAnswer
        userAnswerTexts={userAnswerTexts}
        isAnswerCorrect={isAnswerCorrect}
        userAnswerIndices={userAnswerIndices}
        correctAnswerIndices={correctAnswerIndices}
      />
    </ReviewQuestionWrapper>
  );
};

export default SequencingReview;

